import React, { useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { rem } from 'polished';
import { Grid, GridItem } from '../../helpers';
import { ModuleHeader } from '../../primitives';
import TeaserCard, {
  TeaserCardProps,
} from '../../components/TeaserCard/TeaserCard';
import { colors } from '../../core/styles/theme/default';
import Spacer from '../../helpers/Spacer';
import Slider from '../Slider';
import { sampleSize } from 'lodash';

const StyledTeaserRelated = styled.aside`
  display: block;
`;

export const StyledFilterButton = styled.a(
  ({ isActive }: { isActive: boolean }) => css`
    cursor: pointer;
    background-color: ${colors.blue900};
    padding: ${rem(4)} ${rem(16)};
    display: flex;
    color: ${colors.white};
    border-radius: ${rem(4)};
    font-size: ${rem(12)};
    font-weight: 100;
    letter-spacing: 0.04rem;
    height: ${rem(40)};
    align-items: center;
    border-radius: ${rem(20)};
    user-select: none;

    &:focus {
      &::after {
        border-radius: ${rem(20)};
      }
    }

    ${isActive &&
    css`
      background-color: ${colors.blue800};
    `};
  `,
);

const StyledFilterbar = styled.ul`
  display: flex;
  list-style: none;
  flex-wrap: wrap;
  padding-left: 0;
  margin: 0;

  li {
    padding-right: ${rem(4)};
    margin-bottom: ${rem(6)};
  }
`;

const TeaserRelated = (teaserFundingProps: TeaserRelatedProps) => {
  const [activeTab, setActiveTab] = useState('all');

  if (teaserFundingProps === null) return null;

  const { title, items, renderedPageId, locale } = teaserFundingProps;
  const keywords = items
    .map((item: object[]) => item[0])
    .map(
      (item: {
        informationhub: { contentful_id: string }[];
        storyhub: { contentful_id: string }[];
        podcasthub: { contentful_id: string }[];
      }) => ({
        ...item,
        informationhub:
          item.informationhub?.filter(
            ({ contentful_id }) => contentful_id !== renderedPageId,
          ) || [],
        storyhub:
          item.storyhub?.filter(
            ({ contentful_id }) => contentful_id !== renderedPageId,
          ) || [],
        podcasthub:
          item.podcasthub?.filter(
            ({ contentful_id }) => contentful_id !== renderedPageId,
          ) || [],
      }),
    )
    .filter(
      ({
        informationhub,
        storyhub,
        podcasthub,
      }: {
        informationhub: object[];
        storyhub: object[];
        podcasthub: object[];
      }) => informationhub.length || storyhub.length || podcasthub.length,
    );

  const triggers = keywords.map(
    (
      block: {
        title: string;
        contentful_id: string;
      },
      i: number,
    ) => (
      <li key={i}>
        <StyledFilterButton
          data-trigger={block.title}
          onClick={() =>
            setActiveTab(
              block.contentful_id === activeTab
                ? 'all'
                : `${block.contentful_id}`,
            )
          }
          data-id={block.contentful_id}
          className={`${activeTab === block.contentful_id ? 'active' : ''}`}
          isActive={activeTab === block.contentful_id ? true : false}
          tabIndex={0}
        >
          {block.title}
        </StyledFilterButton>
      </li>
    ),
  );

  let allItems: Array<any> = [];

  keywords.forEach((block: any, i: number) => {
    keywords[i]['items'] = [];

    block.storyhub &&
      keywords[i]['items'].push(
        ...block.storyhub.map((item: any) => {
          return {
            ...item,
            folder: 'stories',
          };
        }),
      );

    block.podcasthub &&
      keywords[i]['items'].push(
        ...block.podcasthub.map((item: any) => {
          return {
            ...item,
            folder: 'podcasts',
          };
        }),
      );

    block.informationhub &&
      keywords[i]['items'].push(
        ...block.informationhub.map((item: any) => {
          return {
            ...item,
            folder: 'informationhub',
          };
        }),
      );

    keywords[i]['items'].sort(
      (a: any, b: any) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

    allItems.push(...keywords[i]['items']);
  });

  allItems.sort(
    (a: any, b: any) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  allItems = Object.values(
    allItems.reduce(
      (acc, cur) => Object.assign(acc, { [cur.contentful_id]: cur }),
      {},
    ),
  );

  const teasers =
    activeTab === 'all'
      ? allItems
      : keywords.filter(
          ({ contentful_id }: { contentful_id: string }) =>
            activeTab === contentful_id,
        )[0].items;

  return teasers.length ? (
    <StyledTeaserRelated>
      <Grid columns={{ zero: 'full', large: 12 }}>
        <GridItem
          size={{ zero: 'full', small: 12, ultra: 8 }}
          push={{ zero: 0, ultra: 2 }}
        >
          <ModuleHeader title={title || ''} />
          <Spacer space="gamma" />
          <StyledFilterbar>{triggers}</StyledFilterbar>
          <Spacer space="mega" />
          <Slider showHiddenSlides>
            {sampleSize(
              teasers.map((data: TeaserCardProps['data']) => {
                return (
                  <TeaserCard
                    data={data}
                    variant="related"
                    key={`tab-${data.contentful_id}-${locale}`}
                  />
                );
              }),
              10,
            )}
          </Slider>
        </GridItem>
      </Grid>
    </StyledTeaserRelated>
  ) : null;
};

export type TeaserRelatedProps = {
  items: any;
  title: string | null;
  renderedPageId: string | null;
  locale: string;
} | null;

export default TeaserRelated;
